import { useQuery } from '@tanstack/react-query';
import {
  type GeoJSONSource,
  Marker,
  type Map as MapLibreMap,
  type MapMouseEvent,
} from 'maplibre-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AddPinForm } from '@/components/AddPinForm';
import { CategoryFilter } from '@/components/CategoryFilter';
import { Footer } from '@/components/Footer';
import { InfoNotice } from '@/components/ConsentLayer';
import { LayerPanel } from '@/components/LayerPanel';
import { MapView } from '@/components/MapView';
import { PinDetail } from '@/components/PinDetail';
import { fetchLayer, fetchSnapshot, type Pin } from '@/lib/api';
import { PODNET, PODPORA, countWith } from '@/lib/plural';
import {
  PIN_CLUSTERS,
  PIN_POINTS,
  PIN_SOURCE,
  addOverlay,
  addPinLayers,
  applyCategoryFilter,
  removeOverlay,
  updatePins,
} from '@/map/layers';

export function MapPage() {
  const mapRef = useRef<MapLibreMap | null>(null);
  const draftMarker = useRef<Marker | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const [selectedPinId, setSelectedPinId] = useState<number | null>(null);
  const [visibleCategories, setVisibleCategories] = useState<ReadonlySet<string> | null>(null);
  const [activeLayers, setActiveLayers] = useState<ReadonlySet<string>>(new Set());
  const [loadingLayers, setLoadingLayers] = useState<ReadonlySet<string>>(new Set());
  const [draft, setDraft] = useState<{ lat: number; lon: number } | null>(null);

  // "Picking" is the interval between clicking "Přidat problém" and clicking a
  // spot on the map. The layer click handlers below are registered once and
  // would otherwise close over a stale `picking`, so a ref mirrors the state
  // for them to read.
  const [picking, setPicking] = useState(false);
  const pickingRef = useRef(false);
  const pickingBannerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    pickingRef.current = picking;
    if (picking) pickingBannerRef.current?.focus();
  }, [picking]);

  const { data: snapshot, isLoading, isError, error } = useQuery({
    queryKey: ['snapshot'],
    queryFn: fetchSnapshot,
  });

  const pins = useMemo(() => snapshot?.pins ?? [], [snapshot]);
  const categories = useMemo(() => snapshot?.categories ?? [], [snapshot]);

  // A pin counts once under every category it carries, so these numbers can
  // sum to more than the total pin count. That is the honest reading of "how
  // many problems touch this area", and the header keeps the distinct count.
  const countsByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const pin of pins) {
      for (const slug of pin.categories) counts[slug] = (counts[slug] ?? 0) + 1;
    }
    return counts;
  }, [pins]);

  const selectedPin = useMemo(
    () => pins.find((p) => p.id === selectedPinId) ?? null,
    [pins, selectedPinId],
  );

  const handleReady = useCallback((map: MapLibreMap) => {
    mapRef.current = map;
    setMapReady(true);
  }, []);

  /* -------- pins on the map -------- */

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !snapshot) return;

    if (!map.getSource(PIN_SOURCE)) {
      addPinLayers(map, pins, categories);

      // Picking mode owns clicks while it is active — placing the draft must
      // win over selecting a pin or zooming into a cluster underneath it.
      map.on('click', PIN_POINTS, (event) => {
        if (pickingRef.current) return;
        const id = event.features?.[0]?.properties?.id;
        if (typeof id === 'number') setSelectedPinId(id);
        else if (typeof id === 'string') setSelectedPinId(Number(id));
      });

      // Clicking a cluster zooms to the zoom level at which it breaks apart,
      // which is the only interaction that makes a cluster useful rather than
      // an obstacle.
      map.on('click', PIN_CLUSTERS, (event) => {
        if (pickingRef.current) return;
        const feature = event.features?.[0];
        const clusterId = feature?.properties?.cluster_id;
        if (clusterId == null) return;
        const source = map.getSource(PIN_SOURCE) as GeoJSONSource | undefined;
        void source?.getClusterExpansionZoom(Number(clusterId)).then((zoom) => {
          map.easeTo({
            center: (feature!.geometry as GeoJSON.Point).coordinates as [number, number],
            zoom,
          });
        });
      });

      for (const layerId of [PIN_POINTS, PIN_CLUSTERS]) {
        map.on('mouseenter', layerId, () => {
          if (pickingRef.current) return;
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', layerId, () => {
          if (pickingRef.current) return;
          map.getCanvas().style.cursor = '';
        });
      }
    } else {
      updatePins(map, pins, categories);
    }
    applyCategoryFilter(map, pins, visibleCategories);
  }, [mapReady, snapshot, pins, categories, visibleCategories]);

  /* -------- toggleable overlays, fetched lazily -------- */

  const toggleLayer = useCallback(
    async (key: string) => {
      const map = mapRef.current;
      if (!map) return;

      if (activeLayers.has(key)) {
        removeOverlay(map, key);
        setActiveLayers((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
        return;
      }

      setLoadingLayers((current) => new Set(current).add(key));
      try {
        const data = await fetchLayer(key);
        addOverlay(map, key, data);
        setActiveLayers((current) => new Set(current).add(key));
      } catch (fetchError) {
        console.error('[layer]', key, fetchError);
      } finally {
        setLoadingLayers((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
      }
    },
    [activeLayers],
  );

  /* -------- adding a problem -------- */

  const startPicking = useCallback(() => {
    setSelectedPinId(null);
    setPicking(true);
  }, []);

  const clearDraft = useCallback(() => {
    draftMarker.current?.remove();
    draftMarker.current = null;
    setDraft(null);
  }, []);

  useEffect(() => {
    return () => {
      draftMarker.current?.remove();
    };
  }, []);

  // Registered only while picking, keyed on `picking` so it attaches and
  // detaches cleanly rather than living for the map's whole lifetime.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !picking) return;

    map.getCanvas().style.cursor = 'crosshair';

    const placeDraftAt = (lng: number, lat: number) => {
      setDraft({ lat, lon: lng });

      draftMarker.current?.remove();
      const marker = new Marker({ draggable: true, color: '#0b3d91' })
        .setLngLat([lng, lat])
        .addTo(map);
      marker.on('dragend', () => {
        const at = marker.getLngLat();
        setDraft({ lat: at.lat, lon: at.lng });
      });
      draftMarker.current = marker;

      setPicking(false);
    };

    const placeOnClick = (event: MapMouseEvent) => {
      placeDraftAt(event.lngLat.lng, event.lngLat.lat);
    };

    // Escape cancels; Enter places the draft at the centre of the current
    // view. That second binding is not a convenience — without it a
    // keyboard-only user cannot submit a problem at all, because every other
    // route into the form requires clicking the canvas. MapLibre's canvas is
    // focusable and pans with the arrow keys, so "pan, then press Enter" is
    // the keyboard equivalent of aiming and clicking.
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPicking(false);
        return;
      }
      if (event.key === 'Enter' && !(event.target instanceof HTMLButtonElement)) {
        event.preventDefault();
        const centre = map.getCenter();
        placeDraftAt(centre.lng, centre.lat);
      }
    };

    map.on('click', placeOnClick);
    document.addEventListener('keydown', onKey);

    return () => {
      map.off('click', placeOnClick);
      document.removeEventListener('keydown', onKey);
      map.getCanvas().style.cursor = '';
    };
  }, [mapReady, picking]);

  const toggleCategory = useCallback(
    (slug: string) => {
      setVisibleCategories((current) => {
        const all = categories.map((c) => c.slug);
        const next = new Set(current ?? all);
        if (next.has(slug)) next.delete(slug);
        else next.add(slug);
        return next.size === all.length ? null : next;
      });
    },
    [categories],
  );

  return (
    // The map owns the viewport. `dvh` rather than `vh` so a mobile browser's
    // collapsing toolbar does not crop the bottom off the map; the notice and
    // the compact legal strip below it are deliberately only a few lines each.
    <div className="flex h-dvh flex-col">
      <a
        href="#map-main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2 focus:shadow"
      >
        Přeskočit na mapu
      </a>

      <header className="z-20 border-b border-slate-200 bg-white px-4 py-3">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Mapa problémů Prahy 6</h1>
          <p className="text-sm text-slate-600">
            {snapshot
              ? `${countWith(snapshot.counts.approved, PODNET)} · ${countWith(snapshot.counts.votes, PODPORA)}`
              : 'Načítám…'}
          </p>
        </div>

        {categories.length > 0 && (
          <div className="mt-3">
            <CategoryFilter
              categories={categories}
              selected={visibleCategories}
              counts={countsByCategory}
              onToggle={toggleCategory}
              onReset={() => setVisibleCategories(null)}
            />
          </div>
        )}
      </header>

      <main id="map-main" className="relative min-h-0 flex-1">
        <MapView onReady={handleReady} />

        {/* Top-left: the only corner free of other map furniture — nav
            control is top-right, scale is bottom-left, attribution is
            bottom-right, the layer panel is bottom-14 left-2, and the
            detail/form slot lives at the bottom. Hidden mid-workflow so it
            never sits behind the picking banner or the open form. */}
        {!picking && !draft && (
          <button
            type="button"
            onClick={startPicking}
            className="absolute left-2 top-2 z-20 min-h-11 rounded-lg bg-brand-700 px-5 py-3 text-base font-semibold text-white shadow-lg hover:bg-brand-600"
          >
            Přidat problém
          </button>
        )}

        {/* `right-14` keeps the picking banner clear of the zoom control in
            the top-right corner; on a narrow screen it wraps to another line
            rather than sliding underneath it. */}
        {picking && (
          <div className="pointer-events-none absolute left-2 right-14 top-2 z-20 flex justify-center">
            {/* Focused on mount: the button that opened this mode has just
                unmounted, so without this the focus ring falls back to
                <body> and the keyboard user loses their place entirely. */}
            <div
              ref={pickingBannerRef}
              tabIndex={-1}
              role="status"
              className="pointer-events-auto flex flex-wrap items-center gap-3 rounded-full bg-slate-900/90 px-4 py-2 text-sm font-medium text-white shadow-lg focus:outline-none"
            >
              <span>
                Klepněte na mapu a označte místo problému.{' '}
                <span className="text-white/70">Nebo klávesou Enter umístěte bod doprostřed mapy.</span>
              </span>
              <button
                type="button"
                onClick={() => setPicking(false)}
                className="rounded-full border border-white/40 px-3 py-1 text-xs font-semibold hover:bg-white/10"
              >
                Zrušit
              </button>
            </div>
          </div>
        )}

        {isLoading && (
          <p
            role="status"
            className="absolute left-1/2 top-4 z-10 -translate-x-1/2 rounded-md bg-white px-4 py-2 text-sm shadow"
          >
            Načítám podněty…
          </p>
        )}
        {isError && (
          <p
            role="alert"
            className="absolute left-1/2 top-4 z-10 -translate-x-1/2 rounded-md bg-white px-4 py-2 text-sm shadow ring-1 ring-red-300"
          >
            Podněty se nepodařilo načíst: {(error as Error).message}
          </p>
        )}

        {snapshot && snapshot.layers.length > 0 && (
          <div className="absolute bottom-14 left-2 z-10 max-w-xs">
            <LayerPanel
              layers={snapshot.layers}
              active={activeLayers}
              loading={loadingLayers}
              onToggle={(key) => void toggleLayer(key)}
            />
          </div>
        )}

        {/* Detail and the submission form share the same slot: only one of
            the two is ever relevant, and stacking them would cover the map. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 p-2 sm:inset-x-auto sm:right-2 sm:w-96">
          <div className="pointer-events-auto">
            {draft && (
              <AddPinForm
                position={draft}
                categories={categories}
                onCancel={clearDraft}
                onSubmitted={clearDraft}
              />
            )}
            {!draft && selectedPin && (
              <PinDetail
                pin={selectedPin}
                categories={categories}
                onClose={() => setSelectedPinId(null)}
              />
            )}
          </div>
        </div>
      </main>

      {/* Below the map: slim strips that never cover it and never block
          interaction with it. */}
      <InfoNotice />
      <Footer compact attributions={snapshot?.layers.map((l) => l.attribution) ?? []} />

      <p className="sr-only">
        <Link to="/pravidla">Pravidla pro podněty</Link>
        <Link to="/soukromi">Ochrana soukromí</Link>
      </p>
    </div>
  );
}

export type { Pin };
